import { parse } from 'vcard4';
import type { ImportVCardRow } from './types';

/**
 * Split a .vcf buffer that may contain multiple cards into individual
 * BEGIN..END blocks, normalize line endings to CRLF (vcard4 requires it),
 * and return a parsed ImportVCardRow per card. Malformed cards are skipped
 * and reported as errors in the return value so the UI can show them.
 */
export interface VCardParseResult {
  rows: ImportVCardRow[];
  errors: Array<{ blockIndex: number; message: string }>;
}

export function parseVcfBuffer(buffer: string): VCardParseResult {
  const normalized = buffer.replace(/\r\n/g, '\n').replace(/\n/g, '\r\n');
  const blocks: string[] = [];
  const re = /BEGIN:VCARD\r\n[\s\S]*?END:VCARD\r\n/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(normalized)) !== null) {
    blocks.push(match[0]);
  }

  const rows: ImportVCardRow[] = [];
  const errors: VCardParseResult['errors'] = [];

  blocks.forEach((block, i) => {
    try {
      const parsed = parse(block) as ParsedCard;
      const row = toImportRow(parsed);
      if (row) rows.push(row);
    } catch (err) {
      errors.push({
        blockIndex: i,
        message: err instanceof Error ? err.message : String(err)
      });
    }
  });

  return { rows, errors };
}

interface ParsedCardEntry {
  property: string;
  parameters: Record<string, string | string[]>;
  value: unknown;
}

interface ParsedCard {
  parsedVcard: ParsedCardEntry[];
}

function toImportRow(card: ParsedCard): ImportVCardRow | null {
  const entries = card.parsedVcard ?? [];
  const fn = entries.find((e) => e.property === 'FN');
  const n = entries.find((e) => e.property === 'N');
  const emails = entries.filter((e) => e.property === 'EMAIL');
  const tels = entries.filter((e) => e.property === 'TEL');
  const adr = entries.find((e) => e.property === 'ADR');

  const displayName = fn?.value as string | undefined
    ?? extractNameFromN(n?.value)
    ?? '';
  if (!displayName.trim()) return null;

  return {
    displayName: displayName.trim(),
    email: pickPreferred(emails) ?? null,
    phone: pickPreferred(tels) ?? null,
    ...(adr?.value && typeof adr.value === 'object' ? addressParts(adr.value as AdrValue) : {})
  };
}

interface AdrValue {
  streetAddress?: string;
  locality?: string;
  region?: string;
  postalCode?: string;
  countryName?: string;
}

function addressParts(adr: AdrValue) {
  return {
    addressLine1: adr.streetAddress || null,
    city: adr.locality || null,
    region: adr.region || null,
    postalCode: adr.postalCode || null,
    country: adr.countryName || null
  };
}

function pickPreferred(entries: ParsedCardEntry[]): string | undefined {
  if (entries.length === 0) return undefined;
  // Prefer entries marked TYPE=pref; otherwise take the first.
  const pref = entries.find((e) => {
    const t = e.parameters?.TYPE;
    const types = Array.isArray(t) ? t : t ? [t] : [];
    return types.some((v) => v.toLowerCase() === 'pref');
  });
  const chosen = pref ?? entries[0];
  return typeof chosen.value === 'string' ? chosen.value : undefined;
}

interface NValue {
  familyName?: string;
  givenName?: string;
}

function extractNameFromN(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const n = value as NValue;
  const parts = [n.givenName, n.familyName].filter((p) => !!p && p.trim().length > 0);
  return parts.length > 0 ? parts.join(' ') : undefined;
}
